import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { ApiShipmentResponseItem } from "../shipments/shipments.model";

export interface ProductShipmentResponse {
  shipmentId: string;
  productId: string;
  tcId: string;
  tcProductShipmentNo: string[];
  tcProductNo: string;
  tcProductCategoryCode: string;
  tcNetShippingWeight: number;
  tcProductProductionDate: string; // Format: YYYY-MM-DD
  tcProductSupplementaryWeight: number;
  tcProductOrderNo: string[];
  tcProductArticleNo: string;
  tcProductNumberOfUnits: number;
  tcProductUnitsMetric: string;
  tcProductInfoAssociatedTc: string;
  tcAssociatedProductShipmentNo?: string;
}

// Input interface for the product data coming from the API
export interface InputProductData {
  productInfo: {
    tcProductShipmentNo: string[];
    tcProductNo: string;
    tcProductCategoryCode: string;
    tcProductProductionDate: string;
    tcNetShippingWeight: string;
    tcProductSupplementaryWeight: string;
    tcProductOrderNo: string[];
    tcProductArticleNo: string;
    tcProductNumberOfUnits: string;
    tcProductUnitsMetric: string;
    tcProductInfoAssociatedTc: string;
    tcProductOrder: string;
    tcProductCategoryUserSpecificTerm: string;
    tcProductDetailCode: string;
    tcProductDetailUserSpecificTerm: string;
  };
  standardInfo: Array<{
    tcProductStandardLabelOrder: number;
    tcProductStandardLabelGrade: string;
    tcProductCertifiedWeight: string;
  }>;
  lastProcessorInfo: {
    tcIsLastProcessorTECertitfied: string;
    tcProductLastProcessorCountry: string;
    tcProductLastProcessorLicenseNo: string;
    tcProductLastProcessorName: string;
    tcProductLastProcessorTeId: string;
  };
  additionalInfo: {
    tcProductInfoAdditional: string;
    tcProductInfoFabricWeightAndConstruct: string;
    tcProductInfoFabricWeightOfFinishedProduct: string;
    tcProductInfoFiberLength: string;
    tcProductInfoTrimsExcluded: string;
    tcProductAdditives: string[];
    tcProductAdditivesWeight: number;
    tcProductInfoYarnCount: string;
  };
  rawMaterialInfo: Array<{
    tcProductRawMaterialOrder: number;
    tcProductRawMaterialCode: string;
    tcProductRawMaterialPercentage: string;
  }>;
}

// Output interface for the mapped payload
export interface MappedProductPayload {
  tcId: string;
  tcNetShippingWeight: number;
  tcProductInformation: Array<{
    tcAssociatedProductShipmentNo: string;
    ocsProcessor: string;
    tcProductNoId: string;
    tcProductOrder: string;
    tcProductNo: string;
    tcProductOrderNo: string[];
    tcProductArticleNo: string;
    tcProductNumberOfUnits: number;
    tcProductUnitsMetric: string;
    tcProductNetShippingWeight: number;
    tcProductSupplementaryWeight: number;
    tcProductProductionDate: string;
    tcProductCategoryCode: string;
    tcProductCategoryCodeDescription?: string;
    tcProductCategoryUserSpecificTerm: string;
    tcProductDetailCode: string;
    tcProductDetailCodeDescription?: string;
    tcProductDetailUserSpecificTerm: string;
    tcProductInfoTrimsExcluded: string;
    tcProductInfoAssociatedTc: string;
    tcProductInfoFiberLength: string;
    tcProductInfoYarnCount: string;
    tcProductInfoFabricWeightAndConstruct: string;
    tcProductInfoFabricWeightOfFinishedProduct: string;
    tcIsLastProcessorTECertitfied: string;
    tcProductLastProcessorName: string;
    tcProductLastProcessorTeId: string;
    tcProductLastProcessorLicenseNo: string;
    tcProductLastProcessorCountry: string;
    tcProductLastProcessorCountryDescription?: string;
    tcProductInfoAdditional: string;
    tcProductAdditives: string[];
    tcProductAdditivesWeight: number;
    tcProductShipmentNo: string[];
    tcProductStandardInformation: Array<{
      tcProductStandardLabelOrder: string;
      tcProductStandardLabelGrade: string;
      tcProductCertifiedWeight: number;
      additionalProp1: Record<string, unknown>;
    }>;
    tcProductRawMaterialInformation: Array<{
      tcProductRawMaterialOrder: string;
      tcProductRawMaterialCode: string;
      tcProductRawMaterialPercentage: number;
      additionalProp1: Record<string, unknown>;
    }>;
    additionalProp1: Record<string, unknown>;
  }>;
  additionalProp1: Record<string, unknown>;
}

/**
 * Maps the provided product data into the desired payload structure.
 *
 * @param data - The input product data.
 * @returns The mapped payload.
 */
export const mapProductData = (
  data: any,
  tcId: string,
  tcShipmentDocNo: string[],
  productId?: string | null
): MappedProductPayload => {
  const {
    productInfo,
    standardInfo,
    lastProcessorInfo,
    additionalInfo,
    rawMaterialInfo,
  } = data;
  return {
    // Top-level tcId can be set as needed. Here, it is a placeholder.
    tcId: tcId,
    ...(productId ? { productId } : {}),
    // Convert top-level net shipping weight to a number.
    tcNetShippingWeight: Number(productInfo.tcNetShippingWeight),
    tcProductInformation: [
      {
        // This could be derived from other data; here we use a placeholder.
        ocsProcessor: "string",
        tcAssociatedProductShipmentNo: productInfo.tcProductShipmentNo,
        // Use product number as an ID. Adjust if needed.
        tcProductNoId: productInfo.tcProductNo,
        tcProductOrder: productInfo.tcProductOrder,
        tcProductNo: productInfo.tcProductNo,
        tcProductOrderNo: productInfo.tcProductOrderNo,
        tcProductArticleNo: productInfo.tcProductArticleNo,
        tcProductNumberOfUnits: Number(productInfo.tcProductNumberOfUnits),
        tcProductUnitsMetric: productInfo.tcProductUnitsMetric,
        // You may choose to use a different field for product net shipping weight.
        tcProductNetShippingWeight: Number(productInfo.tcNetShippingWeight),
        tcProductSupplementaryWeight: Number(
          productInfo.tcProductSupplementaryWeight
        ),
        tcProductProductionDate: productInfo.tcProductProductionDate,
        tcProductCategoryCode: productInfo.tcProductCategoryCode,
        tcProductCategoryCodeDescription:
          useTransactionCertificateStore
            .getState()
            .getProductCategoriesStore.find(
              (option: any) =>
                option.value === productInfo.tcProductCategoryCode
            )?.label || "",
        tcProductCategoryUserSpecificTerm:
          productInfo.tcProductCategoryUserSpecificTerm,
        tcProductDetailCode: productInfo.tcProductDetailCode,
        tcProductDetailCodeDescription:
          useTransactionCertificateStore
            .getState()
            .getProductDetailCodeStore.find(
              (option: any) => option.value === productInfo.tcProductDetailCode
            )?.label || "",
        tcProductDetailUserSpecificTerm:
          productInfo.tcProductDetailUserSpecificTerm,
        // Fields from additionalInfo:
        tcProductInfoTrimsExcluded: additionalInfo.tcProductInfoTrimsExcluded,
        tcProductAdditives: additionalInfo.tcProductAdditives,
        tcProductAdditivesWeight: additionalInfo.tcProductAdditivesWeight===""?null:additionalInfo.tcProductAdditivesWeight,
        tcProductInfoAssociatedTc: productInfo.tcProductInfoAssociatedTc,
        tcProductInfoFiberLength: additionalInfo.tcProductInfoFiberLength,
        tcProductInfoYarnCount: additionalInfo.tcProductInfoYarnCount,
        tcProductInfoFabricWeightAndConstruct:
          additionalInfo.tcProductInfoFabricWeightAndConstruct,
        tcProductInfoFabricWeightOfFinishedProduct:
          additionalInfo.tcProductInfoFabricWeightOfFinishedProduct,
        // Fields from lastProcessorInfo:
        tcIsLastProcessorTECertitfied:
          lastProcessorInfo.tcIsLastProcessorTECertitfied,
        tcProductLastProcessorName:
          lastProcessorInfo.tcProductLastProcessorName,
        tcProductLastProcessorTeId:
          lastProcessorInfo.tcProductLastProcessorTeId,
        tcProductLastProcessorLicenseNo:
          lastProcessorInfo.tcProductLastProcessorLicenseNo,
        tcProductLastProcessorCountry:
          lastProcessorInfo.tcProductLastProcessorCountry,
        tcProductLastProcessorCountryDescription:
          lastProcessorInfo.tcProductLastProcessorCountry
            ? useTransactionCertificateStore
                .getState()
                ?.getCountryCodes?.find(
                  (option: any) =>
                    option.value ===
                    lastProcessorInfo.tcProductLastProcessorCountry
                )?.label || ""
            : "",
        // Additional info:
        tcProductInfoAdditional: additionalInfo.tcProductInfoAdditional,
        // Shipment numbers remain unchanged.
        tcProductShipmentNo: tcShipmentDocNo, //doc no
        // Map standardInfo items.
        tcProductStandardInformation: standardInfo.map(
          (item: {
            tcProductStandardLabelOrder: any;
            tcProductStandardLabelGrade: any;
            tcProductCertifiedWeight: any;
            tcProductRawMaterialCodeStandardLabel: any;
          }) => {
            const parser = new DOMParser();
            const decodedString = parser.parseFromString(
              useTransactionCertificateStore
                .getState()
                .getLabelGrades.find(
                  (option: any) =>
                    option.value === item.tcProductStandardLabelGrade
                )?.label || "",
              "text/html"
            ).body.textContent;

            console.log(decodedString); // Outputs "<div>Hello</div>"

            return {
              tcProductStandardLabelOrder: String(
                item.tcProductStandardLabelOrder
              ),
              tcProductStandardLabelGrade: item.tcProductStandardLabelGrade,
              tcProductStandardLabelGradeDescription: decodedString,
              tcProductRawMaterialCodeStandardLabel: item.tcProductRawMaterialCodeStandardLabel || "",
              tcProductCertifiedWeight: Number(item.tcProductCertifiedWeight),
              additionalProp1: {},
            };
          }
        ),
        // Map rawMaterialInfo items.
        tcProductRawMaterialInformation: rawMaterialInfo.map(
          (item: {
            tcProductRawMaterialOrder: any;
            tcProductRawMaterialCode: any;
            tcProductRawMaterialPercentage: any;
          }) => ({
            tcProductRawMaterialOrder: String(item.tcProductRawMaterialOrder),
            tcProductRawMaterialCode: item.tcProductRawMaterialCode,
            tcProductRawMaterialCodeDescription:
              useTransactionCertificateStore
                .getState()
                .getRawMtrlCodes.find(
                  (option: any) =>
                    option.value === item.tcProductRawMaterialCode
                )?.label || "",
            tcProductRawMaterialPercentage: Number(
              item.tcProductRawMaterialPercentage
            ),
            additionalProp1: {},
          })
        ),
        additionalProp1: {},
      },
    ],
    additionalProp1: {},
  };
};

// ----- Example usage -----
/**
 * Function to get shipment ID based on a shipment number (returns single strings, not arrays)
 * @param data - Array of shipment objects
 * @param shipmentNumber - Shipment number to search for (string)
 * @returns Object containing single shipmentId and tcShipmentDocNo
 */
export const getShipmentIdsByNumbers = (
  data: ApiShipmentResponseItem[],
  shipmentNumber: string
): { shipmentId: string[]; tcShipmentDocNo: string[] } => {
  if (!data || !shipmentNumber) {
    return { shipmentId: [], tcShipmentDocNo: [] };
  }

  const matchingShipment = data.filter((shipment) =>
    shipment.tcShipmentNo === shipmentNumber
  );

  return {
    shipmentId: matchingShipment?.map((shipment) => shipment.shipmentId),
    tcShipmentDocNo: matchingShipment.map(
      (shipment) => shipment.tcShipmentDocNo
    ),
  };
};

interface Shipment {
  shipmentId: string;
  tcProductId: string; // assuming each shipment has a productId field
  [key: string]: any;
}

/**
 * Get shipment IDs based on a single product ID
 * @param data - shipment data array
 * @param productId - single product ID to search
 * @returns shipment ID array
 */
export const getShipmentIdsByProductId = (
  data: any[],
  productId: string
): string[] => {
  if (!productId) return [];

  return data
    .filter((shipment) => shipment.productId === productId)
    .map((shipment) => shipment.shipmentId);
};

/**
 * Maps product API response data to InputProductData structure.
 *
 * @param product - The product object from tcProductInformation array.
 * @param netShippingWeight - Net shipping weight from API (tcNetShippingWeight).
 * @returns Mapped InputProductData object.
 */
export function mapProductDataToStepData(
  product: any,
  netShippingWeight: number
): any {
  const productInfo = {
    tcProductShipmentNo: product.tcAssociatedProductShipmentNo
      ? Array.isArray(product.tcAssociatedProductShipmentNo)
        ? product.tcAssociatedProductShipmentNo[0]
        : product.tcAssociatedProductShipmentNo
      : "",
    tcProductNo: product.tcProductNo || "",
    tcProductCategoryCode: product.tcProductCategoryCode || "",
    tcProductProductionDate: product.tcProductProductionDate || "",
    tcNetShippingWeight: netShippingWeight?.toString() || "",
    tcProductSupplementaryWeight:
      product.tcProductSupplementaryWeight?.toString() || "",
    tcProductOrderNo: product.tcProductOrderNo || [],
    tcProductArticleNo: product.tcProductArticleNo || "",
    tcProductNumberOfUnits: product.tcProductNumberOfUnits?.toString() || "",
    tcProductUnitsMetric: product.tcProductUnitsMetric || "",
    tcProductInfoAssociatedTc: product.tcProductInfoAssociatedTc || "",
    tcProductOrder: product.tcProductOrder || "",
    tcProductCategoryUserSpecificTerm:
      product.tcProductCategoryUserSpecificTerm || "",
    tcProductDetailCode: product.tcProductDetailCode || "",
    tcProductDetailUserSpecificTerm:
      product.tcProductDetailUserSpecificTerm || "",
  };

  const standardInfo = (product.tcProductStandardInformation || []).map(
    (item: any) => ({
      tcProductStandardLabelOrder:
        Number(item.tcProductStandardLabelOrder) || 0,
      tcProductStandardLabelGrade: item.tcProductStandardLabelGrade || "",
      tcProductRawMaterialCodeStandardLabel: item.tcProductRawMaterialCodeStandardLabel || "",
      tcProductCertifiedWeight: item.tcProductCertifiedWeight?.toString() || "",
    })
  );

  const lastProcessorInfo = {
    tcIsLastProcessorTECertitfied: product.tcIsLastProcessorTECertitfied || "Y", // Add value if available
    tcProductLastProcessorCountry: product.tcProductLastProcessorCountry || "",
    tcProductLastProcessorLicenseNo:
      product.tcProductLastProcessorLicenseNo || "",
    tcProductLastProcessorName: product.tcProductLastProcessorName || "",
    tcProductLastProcessorTeId: product.tcProductLastProcessorTeId || "",
  };

  const additionalInfo = {
    tcProductInfoAdditional: product.tcProductInfoAdditional || "",
    tcProductInfoFabricWeightAndConstruct:
      product.tcProductInfoFabricWeightAndConstruct || "",
    tcProductInfoFabricWeightOfFinishedProduct:
      product.tcProductInfoFabricWeightOfFinishedProduct || "",
    tcProductInfoFiberLength: product.tcProductInfoFiberLength || "",
    tcProductInfoTrimsExcluded: product.tcProductInfoTrimsExcluded || "",
    tcProductAdditives: product.tcProductAdditives || [],
    tcProductAdditivesWeight: product.tcProductAdditivesWeight,
    tcProductInfoYarnCount: product.tcProductInfoYarnCount || "",
  };

  const rawMaterialInfo = (product.tcProductRawMaterialInformation || []).map(
    (item: any) => ({
      tcProductRawMaterialOrder: Number(item.tcProductRawMaterialOrder) || 0,
      tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage:
        item.tcProductRawMaterialPercentage?.toString() || "",
      tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
    })
  );

  return {
    productInfo,
    standardInfo,
    lastProcessorInfo,
    additionalInfo,
    rawMaterialInfo,
  };
}
